import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { mapSupabaseError, getUserFriendlyMessage } from '../lib/errors';
import { captureError } from '../lib/sentry';
import { useToast } from '../components/Toast';

export function useDashboardStats() {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dashboard_stats')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'dashboard', action: 'fetch_stats' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 5,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

export function usePaymentStats() {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['payment-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('items')
        .select('total_cost_gel, total_paid_gel, payment_status');

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'dashboard', action: 'fetch_payment_stats' }
        });
        throw apiError;
      }

      const items = data || [];
      const itemsWithDebt = items.filter(
        item => item.payment_status === 'unpaid' || item.payment_status === 'partial'
      );
      
      const totalUnpaid = itemsWithDebt.reduce((sum, item) => {
        const totalCost = parseFloat(item.total_cost_gel) || 0;
        const totalPaid = parseFloat(item.total_paid_gel) || 0;
        return sum + Math.max(0, totalCost - totalPaid);
      }, 0);

      return {
        total_unpaid: totalUnpaid,
        items_with_debt: itemsWithDebt.length
      };
    },
    staleTime: 1000 * 60 * 1,
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// აქაუნთების ლიმიტები (ალტერნატიული)
export function useAccountMonthlyLimits() {
  const toast = useToast();
  
  return useQuery({
    queryKey: ['account-monthly-limits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_monthly_limits')
        .select('*')
        .order('current_month_total', { ascending: false });

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, { 
          tags: { feature: 'dashboard', action: 'fetch_limits' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 2, // 2 წუთი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}
